import { useEffect, useMemo, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LoadingBlock } from '@/components/PageHeader';
import { mockList } from '@/lib/mock/db';
import { isWorking } from '@/lib/personnel';
import { useDepartments } from '@/lib/queries';
import { queryKeys } from '@/lib/queryKeys';
import type { Personnel, PersonnelAssignment } from '@/lib/types';
import { useIsMobile } from '@/lib/utils/useIsMobile';
import { compareMembers } from '@/pages/hr/placementModel';
import { useHqSeatWrite, useHqSeats, type HqSeat } from '@/pages/hr/useHqSeats';

/**
 * 본사 자리배치도 — 도면 위의 의자 자리에 사람을 끌어다 앉히는 판 (V392 · 사용자 지시 2026-10-01).
 *
 * <h2>PDF 가 아니라 판이다</h2>
 * 예전 자리배치도는 자료실의 PDF 평면도에 이름을 <b>글자 주석</b>으로 적는 것이었다(V72). 글자라
 * 사람과 이어지지 않았고, 인사자료의 자리 세 칸과 서로를 몰랐다. 이제 도면은 그림({@link FLOORS})이고
 * 자리는 서버의 줄이다 — 조직도·회의실 배치도처럼 <b>자리는 좌표로 두고 내용만 잇는다</b>.
 *
 * <h2>두 가지 끌기</h2>
 * <ul>
 *   <li><b>사람 끌기</b>(기본) — 오른쪽 목록의 카드나 도면 위의 이름표를 빈 자리에 놓으면 앉고,
 *       목록으로 되돌리면 비운다. 전술판과 같은 HTML5 끌기다.</li>
 *   <li><b>자리 편집</b>(스위치) — 도면을 누르면 자리가 하나 생기고, 자리를 끌면 옮겨지고, × 로 지운다.
 *       인식에서 빠진 임원 의자·새로 들인 책상을 위한 길이다. 서식 도장 배치기와 같은 포인터 끌기다.</li>
 * </ul>
 * 둘을 한 번에 켜지 않는다 — 같은 손짓이 「사람을 옮긴다」 와 「자리를 옮긴다」 로 갈리면 어느 쪽이
 * 일어날지 손이 모른다.
 *
 * <h2>좌표는 그림의 비율이다</h2>
 * 자리의 (x, y) 는 0~1 이라 그림을 몇 픽셀로 그리든 같은 의자를 가리킨다. 확대는 판 전체를
 * transform 으로 키우고, 이름표는 같은 비율 자리에 따라온다.
 *
 * <h2>휠과 끌기로 본다 (사용자 지시 2026-10-01)</h2>
 * 확대 알약(150·200·300%) 대신 <b>휠이 커서 자리를 중심으로</b> 키우고 줄이며, 빈 곳을 끌면 판이
 * 움직인다. 알약은 「맞춤」 하나만 남겨 되돌리는 자리로 쓴다. 휠 리스너는 React 가 passive 로
 * 붙이므로 직접 단다 — 그러지 않으면 페이지가 같이 스크롤된다.
 *
 * <p>빈 곳 끌기와 「자리 편집」의 누르기는 같은 손짓에서 갈린다: 4px 넘게 움직였으면 끌기(판 이동),
 * 안 움직였으면 누름(자리 추가). 이름표 위에서 시작한 손짓은 이름표가 먼저 먹는다(`data-seat`).
 *
 * <h2>고칠 수 있는 사람</h2>
 * 최고관리자·인사총무팀 — 서버가 답한다({@code canEdit}). 그 밖의 사람에게는 보는 판이다.
 */
export const FLOORS = [
  { floor: '3층', image: '/hq-floorplan/3f.png' },
  { floor: '5층', image: '/hq-floorplan/5f.png' },
] as const;

/** 그림 두 장의 비율(4000×1507). 그림이 오기 전에도 판의 높이가 잡혀 있어야 이름표가 덜컥거리지 않는다. */
const IMAGE_RATIO = 1507 / 4000;

const MIN_SCALE = 1;
const MAX_SCALE = 6;
/** 찾는 사람의 자리로 들어갈 때의 배율. 의자 한 줄이 창에 들어오고 이름표가 읽히는 크기다. */
const FOCUS_SCALE = 2.5;

/** 판의 자세 — 맞춤 폭 기준 배율과, 그 배율에서 왼쪽 위가 밀려난 거리(px). */
type View = { scale: number; tx: number; ty: number };
const FIT: View = { scale: 1, tx: 0, ty: 0 };

type DragPayload = { personnelId: string; fromSeatId: string | null };

export function SeatMapView({
  focusPersonnelId = null,
  readOnly = false,
}: {
  /** 직원찾기 명함에서 열었을 때 — 그 사람의 층을 먼저 펴고 이름표를 두드러지게 그린다. */
  focusPersonnelId?: string | null;
  /**
   * 보기만 — 직원찾기 모달(사용자 지시 2026-10-01 「직원찾기 모달에서는 자리 편집은 없고, 인사관리에서만」).
   * 권한이 있어도 사람 끌기·자리 편집·「자리 없음」 목록을 세우지 않는다. 고치는 자리는 인력배치표 하나다.
   */
  readOnly?: boolean;
}) {
  const seatMap = useHqSeats();
  const { data: personnel = [], isPending: personnelPending } = useQuery({
    queryKey: queryKeys.personnel,
    queryFn: () => mockList('personnel'),
  });
  const { data: assignments = [], isPending: assignmentsPending } = useQuery({
    queryKey: queryKeys.personnelAssignments,
    queryFn: () => mockList('personnelAssignments'),
  });
  const { data: departments = [] } = useDepartments();
  const write = useHqSeatWrite();
  const isMobile = useIsMobile();

  const seats = useMemo(() => seatMap.data?.seats ?? [], [seatMap.data]);
  const canEdit = !readOnly && (seatMap.data?.canEdit ?? false);

  const focusSeat = useMemo(
    () => (focusPersonnelId ? seats.find((seat) => seat.personnelId === focusPersonnelId) ?? null : null),
    [focusPersonnelId, seats],
  );

  const [floor, setFloor] = useState<string>(FLOORS[0].floor);
  // 찾는 사람의 자리가 다른 층이면 그 층을 편다. 자리가 없으면 첫 층 그대로다.
  useEffect(() => {
    if (focusSeat) setFloor(focusSeat.floor);
  }, [focusSeat]);

  const [view, setView] = useState<View>(FIT);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');

  /** 끌고 있는 사람. dataTransfer 는 drop 전까지 못 읽는 브라우저가 있어 ref 로도 든다(전술판과 같다). */
  const dragRef = useRef<DragPayload | null>(null);
  const [hoverSeatId, setHoverSeatId] = useState<string | null>(null);
  const [listHover, setListHover] = useState(false);

  const personById = useMemo(
    () => new Map(personnel.filter((person) => isWorking(person)).map((person) => [person.id, person])),
    [personnel],
  );

  /** 사람 → 본사 부서 이름(본직 우선). 현장 인원은 비어 있다 — 자리는 본사에만 있다. */
  const deptOf = useMemo(() => {
    const map = new Map<string, string>();
    const sorted = [...assignments].sort((a, b) => (a.assignmentRole === 'primary' ? -1 : 0) - (b.assignmentRole === 'primary' ? -1 : 0));
    for (const assignment of sorted) {
      if (assignment.assignmentType !== 'department' || map.has(assignment.personnelId)) continue;
      map.set(assignment.personnelId, assignment.assignmentName);
    }
    return map;
  }, [assignments]);

  const seatedIds = useMemo(() => new Set(seats.map((seat) => seat.personnelId).filter(Boolean)), [seats]);

  /**
   * 아직 자리가 없는 본사 사람 — 부서별로 묶는다. 자리를 정하는 사람은 팀 단위로 앉히므로 이름순
   * 한 줄보다 부서 묶음이 손에 맞는다. 묶음 차례는 부서관리의 순서, 묶음 안은 명부의 순서다.
   */
  const unseatedGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const byDept = new Map<string, Personnel[]>();
    for (const person of personById.values()) {
      if (seatedIds.has(person.id)) continue;
      const dept = deptOf.get(person.id);
      if (!dept) continue;
      if (query && !`${person.name} ${person.rank ?? ''} ${dept}`.toLowerCase().includes(query)) continue;
      byDept.set(dept, [...(byDept.get(dept) ?? []), person]);
    }
    const order = new Map(departments.map((department, index) => [department.name, index]));
    return [...byDept.entries()]
      .sort(([a], [b]) => (order.get(a) ?? 999) - (order.get(b) ?? 999))
      .map(([dept, people]) => ({
        dept,
        people: people.sort((a, b) => compareMembers(toMember(a, assignments), toMember(b, assignments))),
      }));
  }, [assignments, deptOf, departments, personById, search, seatedIds]);

  const floorSeats = seats.filter((seat) => seat.floor === floor);
  const image = FLOORS.find((entry) => entry.floor === floor)?.image ?? FLOORS[0].image;

  /**
   * 그림 상자 — 자리 좌표(0~1)를 픽셀로 바꾸는 기준. getBoundingClientRect 는 transform 이 적용된
   * 상자를 주므로 확대·이동 중에도 같은 식이 맞는다.
   */
  const boardRef = useRef<HTMLDivElement>(null);
  /** 보이는 창. 휠·끌기는 여기서 받고, 판은 이 안에서 transform 으로 움직인다. */
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointOf = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const board = boardRef.current;
    if (!board) return null;
    const rect = board.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: clamp((clientX - rect.left) / rect.width),
      y: clamp((clientY - rect.top) / rect.height),
    };
  };

  /** 창 밖으로 판이 다 나가지 않게 — 맞춤(1배)에서는 (0,0) 고정, 키우면 넘친 만큼만 민다. */
  const settle = (next: View): View => {
    const viewport = viewportRef.current;
    if (!viewport) return next;
    const width = viewport.clientWidth;
    const height = viewport.clientHeight;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale));
    return {
      scale,
      tx: Math.min(0, Math.max(width - width * scale, next.tx)),
      ty: Math.min(0, Math.max(height - height * scale, next.ty)),
    };
  };

  const loading = seatMap.isPending || personnelPending || assignmentsPending;

  /*
    층을 바꾸면 맞춤으로 — 다른 층의 구석을 그대로 보여 주면 어디를 보고 있는지 모른다.

    <p>찾는 사람의 층이면 <b>그 자리로 들어간다</b>(사용자 지시 2026-10-01 「모달에서 자리배치도 보기
    했으면 내 위치가 보여야지」). 맞춤 크기의 이름표는 손톱만 해서 어디인지 눈에 안 띈다 — 그 자리를
    창 가운데에 두고 {@link FOCUS_SCALE} 배로 키운다. 창이 있어야 거리를 셀 수 있으므로 로딩이 끝난
    뒤에 한 번 더 돈다.
  */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!focusSeat || focusSeat.floor !== floor || !viewport) {
      setView(FIT);
      return;
    }
    const width = viewport.clientWidth;
    const height = viewport.clientHeight;
    setView(settle({
      scale: FOCUS_SCALE,
      tx: width / 2 - focusSeat.posX * width * FOCUS_SCALE,
      ty: height / 2 - focusSeat.posY * height * FOCUS_SCALE,
    }));
  }, [floor, focusSeat, loading]);

  /*
    휠 — 커서 밑의 점이 제자리에 남도록 배율과 이동을 함께 바꾼다. passive:false 라야 preventDefault 가 먹는다.

    <p>★ {@code loading} 에 걸어 둔다. 처음 렌더는 로딩 칸이라 창이 아직 없고, 빈 deps 로 두면 그때
    한 번 돌고 끝나 리스너가 영영 안 붙는다 — 실제로 그렇게 「휠이 안 된다」 였다(2026-10-01).
  */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const cx = event.clientX - rect.left;
      const cy = event.clientY - rect.top;
      setView((current) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, current.scale * Math.exp(-event.deltaY * 0.0015)));
        const ratio = scale / current.scale;
        return settle({ scale, tx: cx - (cx - current.tx) * ratio, ty: cy - (cy - current.ty) * ratio });
      });
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [loading]);

  /** 빈 곳을 누른 손짓. 움직이면 판 이동, 안 움직이고 놓으면(편집 중) 자리 추가. */
  const panRef = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean } | null>(null);
  const onBoardPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-seat]')) return;
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    panRef.current = { x: event.clientX, y: event.clientY, tx: view.tx, ty: view.ty, moved: false };
  };
  const onBoardPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (!pan) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    if (!pan.moved && Math.hypot(dx, dy) < 4) return;
    pan.moved = true;
    setView((current) => settle({ scale: current.scale, tx: pan.tx + dx, ty: pan.ty + dy }));
  };
  const onBoardPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    panRef.current = null;
    if (!pan) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (!pan.moved) addSeatAt(event);
  };

  const dropPerson = (seat: HqSeat) => {
    const payload = dragRef.current;
    dragRef.current = null;
    setHoverSeatId(null);
    if (!payload || !canEdit || editing) return;
    if (seat.personnelId === payload.personnelId) return;
    if (seat.personnelId) {
      const sitting = personById.get(seat.personnelId)?.name ?? '앉은 사람';
      if (!window.confirm(`${sitting} 을(를) 비우고 넣을까요?`)) return;
    }
    write.assign.mutate({ seatId: seat.id, personnelId: payload.personnelId });
  };

  const dropToList = () => {
    const payload = dragRef.current;
    dragRef.current = null;
    setListHover(false);
    if (!payload?.fromSeatId || !canEdit || editing) return;
    write.assign.mutate({ seatId: payload.fromSeatId, personnelId: null });
  };

  /** 편집 중 도면의 빈 곳을 누르면 자리가 하나 선다. 이름표 위의 클릭은 이름표가 먼저 먹는다. */
  const addSeatAt = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!editing || !canEdit || write.create.isPending) return;
    const point = pointOf(event.clientX, event.clientY);
    if (!point) return;
    write.create.mutate({ floor, posX: round5(point.x), posY: round5(point.y) });
  };

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-4">
      {/* 층·확대·편집 스위치 — 알약은 이 앱에서 「목록 조건」 이고, 편집은 스위치라 오른쪽 끝에 따로 선다. */}
      <div className="card p-4 no-print flex flex-wrap items-center gap-3" data-tour="page-filters">
        <div className="flex gap-1 flex-shrink-0">
          {FLOORS.map((entry) => (
            <button
              key={entry.floor}
              onClick={() => setFloor(entry.floor)}
              aria-pressed={floor === entry.floor}
              className={`selector-chip${floor === entry.floor ? ' active' : ''}`}
            >
              {entry.floor}
            </button>
          ))}
        </div>
        <button
          onClick={() => setView(FIT)}
          aria-pressed={view.scale === 1}
          className={`selector-chip${view.scale === 1 ? ' active' : ''}`}
          style={{ fontSize: '12px' }}
        >
          맞춤{view.scale > 1 ? ` · ${Math.round(view.scale * 100)}%` : ''}
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {floorSeats.filter((seat) => seat.personnelId).length} / {floorSeats.length}석
        </span>
        {/* 찾는 사람에게 자리가 없으면 그 사실을 적는다 — 빈 도면만 보여 주면 「못 찾은 것」과 구분이 안 된다. */}
        {focusPersonnelId && !focusSeat && (
          <span className="badge badge-gray" style={{ fontSize: '11px' }}>자리 미정</span>
        )}
        {canEdit && (
          <button
            type="button"
            className={editing ? 'btn-primary' : 'btn-secondary'}
            style={{ marginLeft: 'auto', fontSize: '12px', padding: '5px 12px' }}
            aria-pressed={editing}
            onClick={() => setEditing((value) => !value)}
          >
            {editing ? '자리 편집 끝' : '자리 편집'}
          </button>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile || readOnly ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 260px',
          gap: '16px',
          alignItems: 'start',
        }}
      >
        {/* 도면. 창은 맞춤 크기로 고정이고, 판이 그 안에서 휠·끌기로 움직인다 — 조직도의 「한 화면」 규칙은 맞춤이 지킨다. */}
        <div
          ref={viewportRef}
          className="card"
          onPointerDown={onBoardPointerDown}
          onPointerMove={onBoardPointerMove}
          onPointerUp={onBoardPointerUp}
          onPointerCancel={() => (panRef.current = null)}
          style={{
            overflow: 'hidden',
            aspectRatio: `${1 / IMAGE_RATIO}`,
            cursor: panRef.current?.moved ? 'grabbing' : editing ? 'crosshair' : view.scale > 1 ? 'grab' : 'default',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <div
            ref={boardRef}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: `${1 / IMAGE_RATIO}`,
              transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              src={image}
              alt=""
              draggable={false}
              style={{ display: 'block', width: '100%', height: '100%', pointerEvents: 'none' }}
            />
            {floorSeats.map((seat) => (
              <SeatMarker
                key={seat.id}
                seat={seat}
                scale={view.scale}
                person={seat.personnelId ? personById.get(seat.personnelId) ?? null : null}
                editing={editing && canEdit}
                movable={canEdit && !editing}
                focused={seat.id === focusSeat?.id}
                hovered={hoverSeatId === seat.id}
                pointOf={pointOf}
                onDragStart={(payload) => (dragRef.current = payload)}
                onDragOver={() => setHoverSeatId(seat.id)}
                onDragLeave={() => setHoverSeatId((current) => (current === seat.id ? null : current))}
                onDrop={() => dropPerson(seat)}
                onMove={(x, y) => write.move.mutate({ seatId: seat.id, posX: round5(x), posY: round5(y) })}
                onRemove={() => {
                  const who = seat.personnelId ? personById.get(seat.personnelId)?.name : null;
                  const question = who ? `${who} 이(가) 앉은 자리를 지울까요?` : '빈 자리를 지울까요?';
                  if (!window.confirm(question)) return;
                  write.remove.mutate(seat.id);
                }}
              />
            ))}
          </div>
        </div>

        {/*
          자리 없는 사람. 여기로 이름표를 끌어다 놓으면 자리를 비운다 — 전술판의 「미배치 · 해제」 칸과
          같은 뜻이다. 사람 끌기가 꺼진 사람(권한 없음·편집 중)에게는 그냥 목록이다.
        */}
        {!readOnly && (
        <aside
          className="card"
          style={{
            outline: listHover ? '2px solid var(--accent)' : 'none',
            maxHeight: isMobile ? undefined : '70vh',
            overflowY: 'auto',
          }}
          onDragOver={(event) => {
            if (!canEdit || editing || !dragRef.current?.fromSeatId) return;
            event.preventDefault();
            setListHover(true);
          }}
          onDragLeave={() => setListHover(false)}
          onDrop={(event) => {
            event.preventDefault();
            dropToList();
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card, white)', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700 }}>자리 없음</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {unseatedGroups.reduce((sum, group) => sum + group.people.length, 0)}명
              </span>
            </div>
            <input
              className="input-field"
              style={{ width: '100%', minWidth: 0, fontSize: '12px' }}
              placeholder="이름 · 직급 · 부서"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          {unseatedGroups.length === 0 ? (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>없음</p>
          ) : (
            unseatedGroups.map((group) => (
              <div key={group.dept} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>
                  {group.dept} · {group.people.length}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {group.people.map((person) => (
                    <PersonChip
                      key={person.id}
                      person={person}
                      movable={canEdit && !editing}
                      focused={person.id === focusPersonnelId}
                      onDragStart={() => (dragRef.current = { personnelId: person.id, fromSeatId: null })}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </aside>
        )}
      </div>
    </div>
  );
}

/**
 * 도면 위의 자리 하나. 사람이 앉아 있으면 이름표, 비어 있으면 점선 동그라미.
 *
 * <p>판을 키워도 마커는 <b>화면 크기 그대로</b>다({@code scale} 의 역배율) — 같이 키우면 600% 에서
 * 동그라미가 의자보다 커져 도면을 가린다(2026-10-01 캡처로 확인). 자리(좌표)는 판을 따라가고
 * 크기만 안 따라간다.
 *
 * <p>편집 중에는 포인터로 끌어 옮기고 × 로 지운다. 누른 자리에서 거의 안 움직였으면 끌기가 아니라
 * 누름이라 아무 일도 하지 않는다 — 그러지 않으면 자리를 살짝 건드릴 때마다 저장이 나간다.
 */
function SeatMarker({
  seat,
  scale,
  person,
  editing,
  movable,
  focused,
  hovered,
  pointOf,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onMove,
  onRemove,
}: {
  seat: HqSeat;
  scale: number;
  person: Personnel | null;
  editing: boolean;
  movable: boolean;
  focused: boolean;
  hovered: boolean;
  pointOf: (clientX: number, clientY: number) => { x: number; y: number } | null;
  onDragStart: (payload: DragPayload) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onMove: (x: number, y: number) => void;
  onRemove: () => void;
}) {
  const [live, setLive] = useState<{ x: number; y: number } | null>(null);
  const pressRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const x = live?.x ?? seat.posX;
  const y = live?.y ?? seat.posY;

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!editing) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    pressRef.current = { x: event.clientX, y: event.clientY, moved: false };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    if (!press) return;
    if (!press.moved && Math.hypot(event.clientX - press.x, event.clientY - press.y) < 4) return;
    press.moved = true;
    const point = pointOf(event.clientX, event.clientY);
    if (point) setLive(point);
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    pressRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (!press?.moved) return;
    const point = pointOf(event.clientX, event.clientY);
    setLive(null);
    if (point) onMove(point.x, point.y);
  };

  const title = person ? person.name : '빈 자리';

  return (
    <div
      data-seat={seat.id}
      title={title}
      draggable={movable && person !== null}
      onDragStart={(event: DragEvent<HTMLDivElement>) => {
        if (!person) return;
        event.dataTransfer.setData('text/plain', person.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart({ personnelId: person.id, fromSeatId: seat.id });
      }}
      onDragOver={(event) => {
        if (!movable) return;
        event.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: 'absolute',
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transform: `translate(-50%, -50%) scale(${1 / scale})`,
        zIndex: focused ? 3 : hovered ? 2 : 1,
        cursor: editing ? 'move' : movable && person ? 'grab' : 'default',
        touchAction: 'none',
      }}
    >
      {person ? (
        <span
          style={{
            display: 'inline-block',
            padding: '1px 6px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 600,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
            color: 'white',
            background: focused ? 'var(--danger)' : 'var(--accent)',
            outline: hovered ? '2px solid var(--danger)' : focused ? '3px solid #fecaca' : 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}
        >
          {person.name}
        </span>
      ) : (
        <span
          style={{
            display: 'block',
            width: editing ? '22px' : '18px',
            height: editing ? '22px' : '18px',
            borderRadius: '50%',
            border: `2px dashed ${hovered ? 'var(--danger)' : 'var(--accent)'}`,
            background: hovered ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.12)',
            fontSize: '9px',
            lineHeight: editing ? '18px' : '14px',
            textAlign: 'center',
            color: 'var(--accent)',
          }}
        >
        </span>
      )}
      {editing && (
        <button
          type="button"
          aria-label="자리 지우기"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            border: 'none',
            background: 'var(--danger)',
            color: 'white',
            fontSize: '10px',
            lineHeight: '16px',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function PersonChip({
  person,
  movable,
  focused,
  onDragStart,
}: {
  person: Personnel;
  movable: boolean;
  focused: boolean;
  onDragStart: () => void;
}) {
  return (
    <span
      draggable={movable}
      onDragStart={(event: DragEvent<HTMLSpanElement>) => {
        event.dataTransfer.setData('text/plain', person.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      title={[person.name, person.rank].filter(Boolean).join(' · ')}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '4px',
        padding: '3px 8px',
        borderRadius: '6px',
        border: `1px solid ${focused ? 'var(--danger)' : 'var(--border)'}`,
        background: focused ? '#fee2e2' : 'var(--bg-hover)',
        fontSize: '12px',
        cursor: movable ? 'grab' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontWeight: 600 }}>{person.name}</span>
      {person.rank && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{person.rank}</span>}
    </span>
  );
}

function toMember(person: Personnel, assignments: PersonnelAssignment[]) {
  const primary = assignments.find((assignment) => assignment.personnelId === person.id && assignment.assignmentType === 'department');
  return {
    name: person.name,
    rank: person.rank,
    position: person.position,
    roleTitle: primary?.roleTitle ?? null,
    fieldName: primary?.fieldName || person.field,
    teamLead: primary?.teamLead ?? false,
  };
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function round5(value: number): number {
  return Math.round(value * 100000) / 100000;
}
